/**
 * Verdict satellite ↔ draft mapping. The editor speaks a FLAT ALL/ANY list
 * of conditions — thresholds and, Phase 38, tag rows ("tagged X") —; a
 * stored tree deeper than one flat group (edited via API) degrades to
 * `advanced` — rendered read-only and NEVER re-sent, so a save can't
 * flatten what it can't express (the automations GateRulesBuilder rule).
 * Defensive parse like the gateway satellite.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROPS_SOURCE, type DropGenVerdictDetail, parseVerdictSources } from '@trafficpulse/shared';

import type { DraftFlatRule, DraftVerdict, DraftVerdictRow } from '../../../../drop-generator.types';
import { isTagRow } from '../../../../drop-generator-rule.types';

export const DEFAULT_VERDICT: DraftVerdict = {
  enabled: false,
  join: 'AND',
  rows: [],
  advanced: false,
  // 20/10/2026 — the "Only when" guards are optional: off (0) until added.
  minSampleSent: 0,
  evaluationDelayMin: 0,
  timesN: 1,
  timesMode: 'TOTAL',
  burnRestDays: null,
  returnTestAmount: null,
  source: DROPS_SOURCE,
};

export function verdictToDraft(v: DropGenVerdictDetail | null): DraftVerdict {
  if (!v) return { ...DEFAULT_VERDICT, rows: [] };
  const parsed = parseTree(v.listVerdictJson);
  return {
    enabled: v.enabled,
    join: parsed.join,
    rows: parsed.rows,
    advanced: parsed.advanced,
    minSampleSent: v.minSampleSent,
    evaluationDelayMin: v.evaluationDelayMin,
    timesN: v.listTimesN ?? 1,
    timesMode: v.listTimesMode === 'ROW' ? 'ROW' : 'TOTAL',
    burnRestDays: v.burnRestDays ?? null,
    returnTestAmount: v.returnTestAmount ?? null,
    source: parseVerdictSources(v.sourcesJson).listVerdict,
  };
}

/** The wire tree for a flat draft; null clears the rules. The caller must
 *  NOT call this while `advanced` — omit the field from the PUT instead. */
export function draftToTree(draft: DraftVerdict): unknown {
  return flatRuleToTree(draft);
}

const leafOf = (row: DraftVerdictRow): Record<string, unknown> => (isTagRow(row) ? { kind: 'tag', tagName: row.tag, ...(row.negate ? { negate: true } : {}) } : { kind: 'condition', metric: row.metric, op: row.op, value: row.value });

/** Any flat ALL/ANY rule (terrible, performing, guard) → the wire tree. */
export function flatRuleToTree(rule: { join: 'AND' | 'OR'; rows: DraftVerdictRow[] }): unknown {
  if (rule.rows.length === 0) return null;
  return {
    kind: 'group',
    op: rule.join,
    children: rule.rows.map((row) => (row.not ? { kind: 'not', child: leafOf(row) } : leafOf(row))),
  };
}

/** A stored tree → the flat rule, or `advanced` when deeper than one group. */
export function parseFlatRule(raw: unknown): DraftFlatRule {
  return parseTree(raw);
}

function parseTree(raw: unknown): DraftFlatRule {
  if (!raw || typeof raw !== 'object') return { join: 'AND', rows: [], advanced: false };
  const node = raw as Record<string, unknown>;
  const children = Array.isArray(node.children) ? node.children : null;
  const flat = node.kind === 'group' && (node.op === 'AND' || node.op === 'OR') && children !== null && children.every((c) => isLeaf(c) || isNotOfLeaf(c));
  if (!flat) return { join: 'AND', rows: [], advanced: true };
  return {
    join: node.op as 'AND' | 'OR',
    rows: (children as Array<Record<string, unknown>>).map((c) => {
      const not = c.kind === 'not';
      const leaf = (not ? c.child : c) as Record<string, unknown>;
      const row: DraftVerdictRow =
        leaf.kind === 'tag'
          ? {
              tag: String(leaf.tagName),
              ...(leaf.negate === true ? { negate: true } : {}),
            }
          : {
              metric: String(leaf.metric),
              op: String(leaf.op),
              value: Number(leaf.value),
            };
      return not ? { ...row, not: true } : row;
    }),
    advanced: false,
  };
}

/** Phase 28 — a NOT around one leaf is still a flat line. */
function isNotOfLeaf(c: unknown): boolean {
  return typeof c === 'object' && c !== null && (c as Record<string, unknown>).kind === 'not' && isLeaf((c as Record<string, unknown>).child);
}

/** A threshold, or (Phase 38) a tag row. */
function isLeaf(c: unknown): boolean {
  if (typeof c !== 'object' || c === null) return false;
  const n = c as Record<string, unknown>;
  if (n.kind === 'tag') return typeof n.tagName === 'string' && n.tagName.length > 0;
  return n.kind === 'condition' && typeof n.metric === 'string' && typeof n.value === 'number';
}
